import { ALL_DISTRICTS } from '@/constants/location_districts';
import { ALL_STATES } from '@/constants/location_states';
import {
  toCleanSlug,
  toInternalDistrictSlug,
  toInternalStateSlug,
} from '@/lib/location_slug_mapper';

export type PanchangTextItem = {
  text: string;
  image?: string;
};

export type PanchangComponentValue = Record<string, PanchangTextItem>;

export type PanchangComponent = {
  name: string;
  value: PanchangComponentValue;
};

export type PanchangPage = {
  name: string;
  components: PanchangComponent[];
};

export type PanchangApiResponse = {
  date: string;
  loc_name?: string;
  data: {
    pages: PanchangPage[];
  };
};

export const PANCHANG_BACKGROUND_URL =
  'https://static.inshorts.com/inshorts/images/v1/variants/jpg/m/2024/11_nov/8_fri/img_1731037161254_554.jpg';

/** District code sent as x-sub-district-code (e.g. DL_ND for New Delhi). */
export const DEFAULT_PANCHANG_DISTRICT_CODE = 'DL_ND';

export const SHUBH_SAMAY_LABELS: Record<string, string> = {
  brahm_muhurt: 'ब्रह्म मुहूर्त',
  abhijit_muhurt: 'अभिजित मुहूर्त',
  amrit_kaal: 'अमृत काल',
  svarth_sidhi_yog: 'सर्वार्थ सिद्धि योग',
};

export const ASHUBH_SAMAY_LABELS: Record<string, string> = {
  panchak: 'पंचक',
  rahu_kaal: 'राहूकाल',
  dumuhurt: 'दुर्मुहूर्त',
  gulik_kaal: 'गुलिक काल',
};

export const SUN_MOON_LABELS: Record<string, string> = {
  suryoday: 'सूर्योदय',
  suryast: 'सूर्यास्त',
  chandroday: 'चन्द्रोदय',
  chandrast: 'चन्द्रास्त',
};

export const TIMINGS_LABELS: Record<string, string> = {
  sun_rise: 'सूर्योदय',
  sun_set: 'सूर्यास्त',
  moon_rise: 'चन्द्रोदय',
  moon_set: 'चन्द्रास्त',
};

export const SUN_TIMING_KEYS = ['sun_rise', 'sun_set'] as const;
export const MOON_TIMING_KEYS = ['moon_rise', 'moon_set'] as const;

const HINDI_MONTHS = [
  'जनवरी',
  'फरवरी',
  'मार्च',
  'अप्रैल',
  'मई',
  'जून',
  'जुलाई',
  'अगस्त',
  'सितंबर',
  'अक्टूबर',
  'नवंबर',
  'दिसंबर',
];

const HINDI_DAYS = [
  'रविवार',
  'सोमवार',
  'मंगलवार',
  'बुधवार',
  'गुरुवार',
  'शुक्रवार',
  'शनिवार',
];

export function formatPanchangDateInHindi(dateStr: string): string {
  const [dayPart, monthPart, yearPart] = dateStr.split('/');
  if (!dayPart || !monthPart || !yearPart) {
    return dateStr;
  }

  const day = Number(dayPart);
  const month = Number(monthPart);
  const year = Number(yearPart);
  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) {
    return dateStr;
  }

  const dayName = HINDI_DAYS[date.getDay()];
  const monthName = HINDI_MONTHS[date.getMonth()];

  return `${dayName}, ${date.getDate()} ${monthName} ${year}`;
}

export function getTodayPanchangDate(): string {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `${day}/${month}/${year}`;
}

export function isValidPanchangDate(date: string): boolean {
  return /^\d{2}\/\d{2}\/\d{4}$/.test(date);
}

export function panchangDateToInputValue(date: string): string {
  const [day, month, year] = date.split('/');
  if (!day || !month || !year) {
    return '';
  }
  return `${year}-${month}-${day}`;
}

export function inputValueToPanchangDate(value: string): string {
  const [year, month, day] = value.split('-');
  if (!day || !month || !year) {
    return '';
  }
  return `${day}/${month}/${year}`;
}

export function buildPanchangDateQuery(date: string): string {
  const today = getTodayPanchangDate();
  if (!date || date === today) {
    return '';
  }
  return `?date=${encodeURIComponent(date)}`;
}

export function getValidatedPanchangState(cityParam: string) {
  const stateInternalSlug = toInternalStateSlug(cityParam);
  if (!stateInternalSlug) {
    return null;
  }
  const state = ALL_STATES[stateInternalSlug];
  if (!state) {
    return null;
  }
  return {
    stateInternalSlug,
    stateCleanSlug: toCleanSlug(stateInternalSlug),
    state,
  };
}

export function getValidatedPanchangDistrict(cityParam: string, districtParam: string) {
  const validatedState = getValidatedPanchangState(cityParam);
  if (!validatedState) {
    return null;
  }
  const districtInternalSlug = toInternalDistrictSlug({
    stateInternalSlug: validatedState.stateInternalSlug,
    districtParam,
  });
  if (!districtInternalSlug) {
    return null;
  }
  const district = ALL_DISTRICTS[validatedState.state.value]?.[districtInternalSlug];
  if (!district) {
    return null;
  }
  return {
    ...validatedState,
    districtInternalSlug,
    districtCleanSlug: toCleanSlug(districtInternalSlug),
    district,
  };
}

export function resolveDistrictCodeForApi({
  stateInternalSlug,
  districtInternalSlug,
}: {
  stateInternalSlug: string;
  districtInternalSlug: string;
}): string {
  const stateCode = ALL_STATES[stateInternalSlug]?.value;
  if (!stateCode) {
    return DEFAULT_PANCHANG_DISTRICT_CODE;
  }
  return (
    ALL_DISTRICTS[stateCode]?.[districtInternalSlug]?.value || DEFAULT_PANCHANG_DISTRICT_CODE
  );
}

export function getPanchangLocationLabel({
  stateInternalSlug,
  districtInternalSlug,
  apiLocationName,
}: {
  stateInternalSlug: string;
  districtInternalSlug: string;
  apiLocationName?: string;
}): string | undefined {
  if (apiLocationName) {
    return apiLocationName;
  }
  const state = ALL_STATES[stateInternalSlug];
  const district = state?.value
    ? ALL_DISTRICTS[state.value]?.[districtInternalSlug]
    : undefined;
  if (state && district) {
    return `${district.name_hi || district.label}, ${state.label_string || state.inline_label}`;
  }
  return undefined;
}

export function buildPanchangDistrictPath({
  stateCleanSlug,
  districtCleanSlug,
  date,
}: {
  stateCleanSlug: string;
  districtCleanSlug: string;
  date?: string;
}): string {
  return `/panchang/${stateCleanSlug}/${districtCleanSlug}${buildPanchangDateQuery(date || getTodayPanchangDate())}`;
}

export function findPage(
  pages: PanchangPage[] | undefined,
  pageName: string,
): PanchangPage | undefined {
  return pages?.find((page) => page.name === pageName);
}

export function findComponentValue(
  page: PanchangPage | undefined,
  componentName: string,
): PanchangComponentValue | undefined {
  return page?.components.find((component) => component.name === componentName)?.value;
}

export function collectIndexedItems(
  value: PanchangComponentValue | undefined,
  prefix: string,
): PanchangTextItem[] {
  if (!value) {
    return [];
  }
  return Object.entries(value)
    .filter(([key, item]) => key.startsWith(prefix) && item?.text)
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    .map(([, item]) => item);
}

export function collectKeyedItems(
  value: PanchangComponentValue | undefined,
  labels: Record<string, string>,
): Array<{ key: string; label: string; item: PanchangTextItem }> {
  if (!value) {
    return [];
  }
  return Object.entries(value)
    .filter(([, item]) => item?.text)
    .map(([key, item]) => ({
      key,
      label: labels[key] || key.replace(/_/g, ' '),
      item,
    }));
}
